import { useEffect, useRef } from "react";

// Converging flow-lines: streams bend into a central node and flow back out to the sides.
// Lightweight: rAF, DPR-capped, pointer-events:none. Palette-safe (ink/white + vinotinto accent).
export const FlowLines = ({
  className = "",
  lineColor = "rgba(23,23,23,0.10)",
  nodeColor = "rgba(23,23,23,0.85)",
  accent = "rgba(158,27,34,0.85)",
  lines = 13,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w = 0, h = 0, dpr = 1;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const rows = Array.from({ length: lines }, (_, i) => ({
      yFrac: lines > 1 ? i / (lines - 1) : 0.5,
      speed: 0.00018 + Math.random() * 0.00016,
      phase: Math.random(),
      accent: i % 4 === 1,
    }));

    const bez = (p0, p1, p2, p3, u) => {
      const m = 1 - u;
      return m * m * m * p0 + 3 * m * m * u * p1 + 3 * m * u * u * p2 + u * u * u * p3;
    };

    const render = (t) => {
      ctx.clearRect(0, 0, w, h);
      // Auto white palette on dark surfaces (live-updates with theme toggle)
      const dark = document.documentElement.classList.contains("dark");
      const lc = dark ? "rgba(255,255,255,0.16)" : lineColor;
      const nc = dark ? "rgba(255,255,255,0.92)" : nodeColor;
      const cx = w / 2, cy = h / 2;
      const pad = h * 0.08;
      const span = h - pad * 2;

      rows.forEach((r) => {
        const sy = pad + r.yFrac * span;
        // Left incoming curve: edge -> node
        const L = [0, sy, w * 0.34, sy, cx - w * 0.14, cy, cx, cy];
        // Right outgoing curve: node -> edge
        const R = [cx, cy, cx + w * 0.14, cy, w * 0.66, sy, w, sy];

        [L, R].forEach((c, side) => {
          ctx.beginPath();
          ctx.moveTo(c[0], c[1]);
          ctx.bezierCurveTo(c[2], c[3], c[4], c[5], c[6], c[7]);
          ctx.strokeStyle = lc;
          ctx.lineWidth = 1;
          ctx.stroke();

          // Arrowhead at right outgoing ends
          if (side === 1) {
            const ux = c[6] - 7, uy = c[7];
            ctx.beginPath();
            ctx.moveTo(c[6], c[7]);
            ctx.lineTo(ux, uy - 3.2);
            ctx.moveTo(c[6], c[7]);
            ctx.lineTo(ux, uy + 3.2);
            ctx.strokeStyle = lc;
            ctx.stroke();
          }

          // Traveling particle
          const dir = side === 0 ? 1 : 1;
          const prog = ((t * r.speed + r.phase + side * 0.5) % 1 + 1) % 1;
          const u = dir === 1 ? prog : 1 - prog;
          const px = bez(c[0], c[2], c[4], c[6], u);
          const py = bez(c[1], c[3], c[5], c[7], u);
          const fade = Math.sin(prog * Math.PI);
          ctx.beginPath();
          ctx.arc(px, py, 1.7, 0, Math.PI * 2);
          ctx.fillStyle = r.accent
            ? accent.replace(/[\d.]+\)$/, `${(0.9 * fade).toFixed(2)})`)
            : nc.replace(/[\d.]+\)$/, `${(0.75 * fade).toFixed(2)})`);
          ctx.fill();
        });
      });

      // Central node with soft pulsing glow
      const pulse = 0.6 + 0.4 * Math.sin(t * 0.0016);
      ctx.save();
      ctx.shadowBlur = 16 * pulse;
      ctx.shadowColor = accent.replace(/[\d.]+\)$/, "0.5)");
      ctx.fillStyle = nc;
      const s = 9;
      ctx.fillRect(cx - s / 2, cy - s / 2, s, s);
      ctx.restore();

      if (!reduce) raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [lines, lineColor, nodeColor, accent]);

  return <canvas ref={ref} className={className} style={{ pointerEvents: "none" }} aria-hidden="true" />;
};
